// src/services/chapterService.tsx
import apiClient from "../api/apiClient";
import { AxiosRequestConfig } from "axios";
import { parseChapterResources } from "./resourceParser"; // NEW: parser to convert raw JSON -> Domain Model
import { ChapterResourcesResponse } from "../models/ChapterResourcesDomain"; // NEW: typed domain response

const TENANT_ID = process.env.REACT_APP_TENANT_ID ?? "1";

// --- Existing Chapter CRUD ---

export async function getChapters(params: { subjectId: number; page?: number; size?: number }) {
    const { subjectId, page = 0, size = 20 } = params;
    const resp = await apiClient.get('/chapters', {
        params: { subjectId, page, size, tenantId: TENANT_ID }
    });
    return resp.data;
}

export async function createChapter(payload: any) {
    const resp = await apiClient.post('/admin/chapters', payload, { params: { tenantId: TENANT_ID } });
    return resp.data;
}

export async function updateChapter(id: number, payload: any) {
    const resp = await apiClient.put(`/admin/chapters/${id}`, payload, { params: { tenantId: TENANT_ID } });
    return resp.data;
}

export async function deleteChapter(id: number) {
    const resp = await apiClient.delete(`/admin/chapters/${id}`, { params: { tenantId: TENANT_ID } });
    return resp.data;
}

// --- Chapter Resources Integration ---

// 1. Fetch All Data (UPDATED: returns parsed ChapterResourcesResponse)
export async function getChapterQuestions(chapterId: number): Promise<ChapterResourcesResponse> {
    const resp = await apiClient.get(`/chapters/${chapterId}/questions`, {
        params: { tenantId: TENANT_ID }
    });
    // USE THE PARSER
    return parseChapterResources(resp.data);
}
// 2. Introduction APIs
export interface IntroDto {
    title: string;
    url: string;
    description?: string;
    question?: string;
    mimeType?: string;
    displayOrder?: number;
}

export async function setIntroductionByUrl(chapterId: number, data: IntroDto) {
    const resp = await apiClient.post(`/admin/chapters/${chapterId}/introduction`, data, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

export async function uploadIntroductionVideo(
    chapterId: number,
    file: File,
    meta: { title: string; description?: string; question?: string },
    onProgress?: (percent: number) => void
) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', meta.title);
    if (meta.description) formData.append('description', meta.description);
    if (meta.question) formData.append('question', meta.question);

    const config: AxiosRequestConfig = {
        params: { tenantId: TENANT_ID },
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                if (onProgress) onProgress(percentCompleted);
            }
        }
    };

    const resp = await apiClient.post(`/admin/chapters/${chapterId}/introduction/upload`, formData, config);
    return resp.data;
}

export async function deleteIntroduction(chapterId: number, deleteStored: boolean = true) {
    const resp = await apiClient.delete(`/admin/chapters/${chapterId}/introduction`, {
        params: { tenantId: TENANT_ID, deleteStored }
    });
    return resp.data;
}

// 2.5 MCQ Video APIs
export async function setMcqVideoByUrl(chapterId: number, data: IntroDto) {
    const resp = await apiClient.post(`/admin/chapters/${chapterId}/mcq-video`, data, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

export async function uploadMcqVideo(
    chapterId: number,
    file: File,
    meta: { title: string; description?: string; question?: string },
    onProgress?: (percent: number) => void
) {
    const formData = new FormData();
    formData.append('file', file);
    if (meta.title) formData.append('title', meta.title);
    if (meta.description) formData.append('description', meta.description);
    if (meta.question) formData.append('question', meta.question);

    const config: AxiosRequestConfig = {
        params: { tenantId: TENANT_ID },
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                if (onProgress) onProgress(percentCompleted);
            }
        }
    };

    const resp = await apiClient.post(`/admin/chapters/${chapterId}/mcq-video/upload`, formData, config);
    return resp.data;
}

export async function deleteMcqVideo(chapterId: number, deleteStored: boolean = true) {
    const resp = await apiClient.delete(`/admin/chapters/${chapterId}/mcq-video`, {
        params: { tenantId: TENANT_ID, deleteStored }
    });
    return resp.data;
}

// 2.7 One Mark Video APIs
export async function setOnemarkVideoByUrl(chapterId: number, data: IntroDto) {
    const resp = await apiClient.post(`/admin/chapters/${chapterId}/onemark-video`, data, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

export async function uploadOnemarkVideo(
    chapterId: number,
    file: File,
    meta: { title: string; description?: string; question?: string },
    onProgress?: (percent: number) => void
) {
    const formData = new FormData();
    formData.append('file', file);
    if (meta.title) formData.append('title', meta.title);
    if (meta.description) formData.append('description', meta.description);
    if (meta.question) formData.append('question', meta.question);

    const config: AxiosRequestConfig = {
        params: { tenantId: TENANT_ID },
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                if (onProgress) onProgress(percentCompleted);
            }
        }
    };

    const resp = await apiClient.post(`/admin/chapters/${chapterId}/onemark-video/upload`, formData, config);
    return resp.data;
}

export async function deleteOnemarkVideo(chapterId: number, deleteStored: boolean = true) {
    const resp = await apiClient.delete(`/admin/chapters/${chapterId}/onemark-video`, {
        params: { tenantId: TENANT_ID, deleteStored }
    });
    return resp.data;
}

// 2.8 One Mark Question (Non-MCQ) APIs
export interface OneMarkQuestionDto {
    question: string;
    description?: string;
    marks: number;
    questionNumber?: number;
}

export async function createOneMarkQuestion(chapterId: number, data: OneMarkQuestionDto, file?: File | null) {
    const formData = new FormData();
    if (file) {
        formData.append('file', file);
    }
    formData.append('data', JSON.stringify(data));

    const resp = await apiClient.post(`/admin/chapters/${chapterId}/onemark-question`, formData, {
        params: { tenantId: TENANT_ID },
        headers: { "Content-Type": "multipart/form-data" }
    });
    return resp.data;
}

export async function updateOneMarkQuestion(chapterId: number, groupId: number, data: OneMarkQuestionDto, file?: File | null) {
    const formData = new FormData();
    if (file) {
        formData.append('file', file);
    }
    formData.append('data', JSON.stringify(data));

    const resp = await apiClient.put(`/admin/chapters/${chapterId}/onemark-question/${groupId}`, formData, {
        params: { tenantId: TENANT_ID },
        headers: { "Content-Type": "multipart/form-data" }
    });
    return resp.data;
}

export async function deleteOneMarkQuestion(chapterId: number, groupId: number) {
    const resp = await apiClient.delete(`/admin/chapters/${chapterId}/onemark-question/${groupId}`, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

// 2.9 Multi-Mark Question APIs (Updated with videoUrl support)
export interface MultiMarkQuestionDto {
    question: string;
    description?: string;
    marks: number;
    questionNumber?: number;
    videoUrl?: string; // New field for URL support
    removeVideo?: boolean; // when true and no new file/url, unlink any existing video
}

export async function createMultiMarkQuestion(
    chapterId: number,
    data: MultiMarkQuestionDto,
    imageFile?: File | null,
    videoFile?: File | null
) {
    const formData = new FormData();
    formData.append('data', JSON.stringify(data));
    if (imageFile) formData.append('image', imageFile);
    if (videoFile) formData.append('video', videoFile);

    const resp = await apiClient.post(`/admin/chapters/${chapterId}/multimark-question`, formData, {
        params: { tenantId: TENANT_ID },
        headers: { "Content-Type": "multipart/form-data" }
    });
    return resp.data;
}

export async function updateMultiMarkQuestion(
    chapterId: number,
    groupId: number,
    data: MultiMarkQuestionDto,
    imageFile?: File | null,
    videoFile?: File | null
) {
    const buildFormData = () => {
        const formData = new FormData();
        formData.append('data', JSON.stringify(data));
        if (imageFile) formData.append('image', imageFile);
        if (videoFile) formData.append('video', videoFile);
        return formData;
    };

    const endpoint = `/admin/chapters/${chapterId}/multimark-question/${groupId}`;
    const config: AxiosRequestConfig = {
        params: { tenantId: TENANT_ID }
    };

    try {
        const resp = await apiClient.put(endpoint, buildFormData(), config);
        return resp.data;
    } catch (error: any) {
        if (error?.response?.status !== 405) {
            throw error;
        }

        // Some deployments/proxies block PUT multipart; support legacy POST update route.
        try {
            const resp = await apiClient.post(endpoint, buildFormData(), config);
            return resp.data;
        } catch (postError: any) {
            if (postError?.response?.status !== 405) {
                throw postError;
            }

            // Last fallback for old stacks using method-override conventions.
            const resp = await apiClient.post(endpoint, buildFormData(), {
                params: { tenantId: TENANT_ID, _method: "PUT" },
                headers: { "X-HTTP-Method-Override": "PUT" }
            });
            return resp.data;
        }
    }
}

export async function deleteMultiMarkQuestion(chapterId: number, groupId: number) {
    const resp = await apiClient.delete(`/admin/chapters/${chapterId}/multimark-question/${groupId}`, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

// --- MCQ Groups & Options ---

export interface McqOptionDto {
    id?: number;
    text: string;
    correct: boolean;
    explanation?: string | null;
}

export interface McqGroupDataDto {
    question: string;
    description?: string;
    marks: number;
    questionNumber?: number;
    options: McqOptionDto[];
}

export async function createMcqGroupWithUpload(chapterId: number, data: McqGroupDataDto, file?: File | null) {
    const formData = new FormData();
    if (file) {
        formData.append('file', file);
    }
    formData.append('data', JSON.stringify(data));

    const resp = await apiClient.post(`/admin/chapters/${chapterId}/mcq/upload`, formData, {
        params: { tenantId: TENANT_ID },
        headers: { "Content-Type": "multipart/form-data" }
    });
    return resp.data;
}

export async function updateMcqGroupWithUpload(chapterId: number, groupId: number, data: McqGroupDataDto, file?: File | null) {
    const formData = new FormData();
    if (file) {
        formData.append('file', file);
    }
    formData.append('data', JSON.stringify(data));

    const resp = await apiClient.put(`/admin/chapters/${chapterId}/mcq/${groupId}/upload`, formData, {
        params: { tenantId: TENANT_ID },
        headers: { "Content-Type": "multipart/form-data" }
    });
    return resp.data;
}

export async function deleteMcqGroup(chapterId: number, groupId: number) {
    const resp = await apiClient.delete(`/admin/chapters/${chapterId}/mcq/${groupId}`, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

export async function createMcqGroup(chapterId: number, payload: any) {
    const resp = await apiClient.post(`/admin/chapters/${chapterId}/mcq`, payload, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

export async function deleteMcqOption(chapterId: number, optionId: number) {
    const resp = await apiClient.delete(`/admin/chapters/resource-groups/${chapterId}/mcq-options/${optionId}`, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

export async function addMcqOption(groupId: number, payload: { text: string; correct: boolean; explanation?: string }) {
    const resp = await apiClient.post(`/admin/chapters/resource-groups/${groupId}/mcq-options`, payload, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

// 5. Resource Groups
export async function createResourceGroup(chapterId: number, payload: any) {
    const resp = await apiClient.post(`/admin/chapters/${chapterId}/resource-groups`, payload, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

export async function updateResourceGroup(chapterId: number, groupId: number, payload: any) {
    const resp = await apiClient.put(`/admin/chapters/${chapterId}/resource-groups/${groupId}`, payload, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

export async function deleteResourceGroup(chapterId: number, groupId: number) {
    const resp = await apiClient.delete(`/admin/chapters/${chapterId}/resource-groups/${groupId}`, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

// 6. Resources
export async function uploadResourceToGroup(chapterId: number, groupId: number, file: File, type: 'VIDEO' | 'IMAGE' = 'VIDEO') {
    const formData = new FormData();
    formData.append('file', file);
    const endpoint = type === 'VIDEO'
        ? `/admin/chapters/${chapterId}/resource-groups/${groupId}/video`
        : `/admin/chapters/${chapterId}/resource-groups/${groupId}/question-image`;

    const resp = await apiClient.post(endpoint, formData, {
        params: { tenantId: TENANT_ID },
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return resp.data;
}

export async function deleteResource(chapterId: number, groupId: number, resourceId: number) {
    const resp = await apiClient.delete(`/admin/chapters/${chapterId}/resource-groups/${groupId}/resources/${resourceId}`, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}
